import type { ComponentChildren, RefObject } from "preact";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "preact/hooks";
import { ApiError, api, type SendRequest, type SendResult } from "./api";
import { SignedOut } from "./auth";
import { type Suggestion, entryAt, forgetContacts, formatContact, loadContacts, replaceEntry, suggest } from "./contacts";
import {
  type Draft,
  MAX_MESSAGE_BYTES,
  addressOf,
  clearDraft,
  draftFiles,
  draftTarget,
  dropDraftFile,
  estimatedSize,
  invalidAddresses,
  keepDraftFiles,
  loadDraft,
  newKey,
  parseAddresses,
  pruneDraftFiles,
  saveDraft,
  startFields,
  toBase64,
} from "./outgoing";
import { size } from "./format";
import { Icon } from "./icons";
import { NetworkError, isNetworkError, isOnline, useOnline } from "./net";
import { loadMessage } from "./offline";
import { queueMail } from "./outbox";
import type { ComposeMode } from "./route";

interface Props {
  mode: ComposeMode;
  /** The message answered or forwarded (null for a new message). */
  source: string | null;
  /** The draft on the device the message is kept as: one kept under this id is taken up again, else one starts. */
  draftId: string;
  /** The Yahoo address: left out of "reply all". */
  me: string | null;
  /** Closed; `kept`: the message stays in the drafts on the device, to be finished later. */
  onClose: (kept: boolean) => void;
  onSent: (result: SendResult) => void;
  /** Put in the outbox: no connection (or no answer); it is sent when the app can reach the server. */
  onQueued: () => void;
  onError: (error: unknown) => void;
}

export const COMPOSE_TITLES: Record<ComposeMode, string> = { new: "New message", reply: "Reply", all: "Reply all", forward: "Forward" };

const SAVE_DELAY_MS = 400;

/** Plain-text mail: a new message, a reply (the original quoted below), reply all or a forward (with the original's
 * attachments). Once written in, the draft is kept on the device as it is written, with the idempotency key made when
 * it was started: sending it again after a lost answer, a reload or a sign-in never sends it twice. The files added
 * from the device are kept with it, so they are still attached when the app is closed and opened again. Closing a
 * message changed here asks whether to save it as a draft (Drafts on this device, to finish and send later) or discard
 * it. Without a connection, or without an answer, Send puts it in the outbox (outbox.ts), which sends it with the same
 * key once the server answers. */
export function Compose({ mode, source, draftId, me, onClose, onSent, onQueued, onError }: Props) {
  const target = draftTarget(mode, source);
  const [draft, setDraft] = useState<Draft | null>(null); // null while the original loads
  const [files, setFiles] = useState<{ id: string; file: File }[]>([]);
  const [lost, setLost] = useState<string[]>([]); // device files of a restored draft that were not kept, to be added again
  const [copies, setCopies] = useState(false);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<{ text: string; sentAlready?: boolean } | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [book, setBook] = useState<Suggestion[]>([]);
  const online = useOnline();
  const bodyField = useRef<HTMLTextAreaElement>(null);
  const toField = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [closing, setClosing] = useState(false); // asking whether to save it as a draft
  const done = useRef(false); // sent, discarded or closed: nothing more is saved
  const edited = useRef(false); // changed since it was opened here: saved as it is written, and closing asks
  const wasKept = useRef(false); // a draft on the device before it was opened here
  const latest = useRef<Draft | null>(null);
  const focused = useRef(false);

  const keep = (d: Draft, withFiles = files, lostNames = lost): Draft => ({
    ...d,
    files: withFiles.map(({ id, file }) => ({ id, name: file.name })),
    lostFiles: lostNames,
    savedAt: Date.now(),
  });
  latest.current = draft && keep(draft);

  // This message's draft from the device, or a new one from the original.
  useEffect(() => {
    let live = true;
    done.current = false;
    edited.current = false;
    focused.current = false;
    setFiles([]);
    setProblem(null);
    setUnavailable(false);
    setClosing(false);
    const stored = loadDraft(draftId);
    const kept = stored?.target === target ? stored : null;
    wasKept.current = kept !== null;
    // a draft under this id for another message (a URL changed by hand) is left alone
    const id = stored && !kept ? newKey() : draftId;
    const start = (fields: Partial<Draft>) => {
      const d: Draft = {
        id, target, key: newKey(), to: "", cc: "", bcc: "", subject: "", body: "", archived: [], files: [], lostFiles: [], touched: false, savedAt: Date.now(), ...fields,
      };
      setDraft(d);
      setLost([]);
      setCopies(Boolean(d.cc));
    };
    const restore = (kept: Draft, found: { id: string; file: File }[], missing: string[]) => {
      setDraft(kept);
      setFiles(found);
      setLost(Array.from(new Set([...kept.lostFiles, ...missing])));
      setCopies(Boolean(kept.cc || kept.bcc));
    };
    void pruneDraftFiles(); // what an earlier draft left
    if (kept && kept.files.length === 0) {
      restore(kept, [], []);
    } else if (kept) {
      // nothing is saved until its files are back, so the draft never loses them
      setDraft(null);
      draftFiles(kept).then(({ files: found, missing }) => live && restore(kept, found, missing));
    } else if (mode === "new" || !source) {
      start({});
    } else {
      setDraft(null);
      loadMessage(source).then(
        (msg) => live && start(startFields(msg, mode, me)),
        (e) => {
          if (!live) return;
          setUnavailable(true);
          onError(e);
        },
      );
    }
    return () => {
      live = false;
    };
  }, [target, draftId]);

  // The address book for suggestions, fetched while the message is started.
  useEffect(() => {
    let live = true;
    loadContacts().then((list) => live && setBook(list));
    return () => {
      live = false;
    };
  }, []);

  // Once written in, kept on the device as it is written, and when the screen is left (back gesture, another message).
  // A reply only opened and left is not kept: it starts from the original again.
  useEffect(() => {
    if (!draft || !edited.current) return;
    const t = setTimeout(() => !done.current && saveDraft(keep(draft)), SAVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [draft, files, lost]);
  useEffect(
    () => () => {
      if (!done.current && edited.current && latest.current) saveDraft(latest.current);
    },
    [],
  );

  // A reply starts above the quoted original, a new message or forward in To.
  useEffect(() => {
    if (!draft || focused.current) return;
    focused.current = true;
    if (mode === "reply" || mode === "all") {
      bodyField.current?.focus({ preventScroll: true });
      bodyField.current?.setSelectionRange(0, 0);
    } else if (!draft.to) {
      toField.current?.focus({ preventScroll: true });
    }
  }, [draft]);

  // The text field grows with its text; the screen scrolls.
  useLayoutEffect(() => {
    const el = bodyField.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [draft?.body]);

  const update = (fields: Partial<Draft>) => {
    edited.current = true;
    setDraft((d) => d && { ...d, ...fields, touched: true });
  };

  /** The files changed: the draft lists them at once, so they are never deleted as files no draft lists. */
  const changeFiles = (next: { id: string; file: File }[], lostNames = lost) => {
    if (!draft) return;
    const d = { ...draft, touched: true };
    edited.current = true;
    setFiles(next);
    setLost(lostNames);
    setDraft(d);
    if (!done.current) saveDraft(keep(d, next, lostNames));
  };

  const addFiles = (list: FileList | null) => {
    if (!draft || !list || list.length === 0) return;
    const added = Array.from(list, (file) => ({ id: newKey(), file }));
    const weight = estimatedSize(draft.body, [...draft.archived.map((a) => a.size), ...[...files, ...added].map((f) => f.file.size)]);
    if (weight > MAX_MESSAGE_BYTES) {
      setProblem({ text: `Too large: Yahoo sends messages of up to 25 MB with their attachments, and this would be about ${size(weight)}.` });
      return;
    }
    changeFiles(
      [...files, ...added],
      lost.filter((name) => !added.some((f) => f.file.name === name)),
    );
    setProblem(null);
    // without room on the device they are attached all the same, but not kept should the app be closed
    keepDraftFiles(added).catch((e) => console.warn("draft:", e));
  };

  const send = async (asNew = false) => {
    if (!draft || sending) return;
    const to = parseAddresses(draft.to);
    const cc = parseAddresses(draft.cc);
    const bcc = parseAddresses(draft.bcc);
    if (to.length === 0) {
      setProblem({ text: "Add a recipient in To." });
      toField.current?.focus();
      return;
    }
    const bad = invalidAddresses([...to, ...cc, ...bcc]);
    if (bad.length > 0) {
      setProblem({ text: `Not an e-mail address: ${bad.join(", ")}` });
      return;
    }
    if (!draft.subject.trim() && !confirm("Send this message without a subject?")) return;
    // "Send this version too" after an earlier version went out: a new message, with a key of its own.
    const message = { ...draft, key: asNew ? newKey() : draft.key, touched: true };
    setDraft(message);
    // kept with its key until it is sent or in the outbox, whatever happens to the answer
    edited.current = true;
    saveDraft(keep(message));
    setSending(true);
    setProblem(null);
    const request: SendRequest = {
      to,
      cc,
      bcc,
      subject: message.subject,
      body: message.body,
      reply_to: (mode === "reply" || mode === "all") && source ? source : undefined,
      forward_of: mode === "forward" && source ? source : undefined,
      attachments: message.archived.map((a) => ({ message_id: a.message_id, part: a.part })),
      idempotency_key: message.key,
    };
    try {
      let result: SendResult | null = null;
      if (isOnline()) {
        try {
          const uploads = await Promise.all(
            files.map(async ({ file }) => ({ filename: file.name, mime: file.type || "application/octet-stream", data: await toBase64(file) })),
          );
          result = await api.send({ ...request, attachments: [...(request.attachments ?? []), ...uploads] });
        } catch (e) {
          if (!isNetworkError(e)) throw e;
        }
      }
      if (!result) {
        // No connection, or no answer (it may have gone out; the key makes sending it again safe): the outbox sends it.
        try {
          await queueMail({ request, draft: { ...message, savedAt: Date.now() }, mode, source }, files.map((f) => f.file));
        } catch (e) {
          console.warn("outbox:", e);
          throw new NetworkError();
        }
        done.current = true;
        clearDraft(message.id);
        onQueued();
        return;
      }
      done.current = true;
      clearDraft(message.id);
      forgetContacts();
      onSent(result);
    } catch (e) {
      if (e instanceof SignedOut) {
        onError(e); // the draft stays on the device for after the sign-in
      } else if (e instanceof ApiError && e.code === "sent_already") {
        setProblem({ text: "An earlier version of this message was sent already; this version was not sent.", sentAlready: true });
      } else if (e instanceof ApiError && e.status === 409) {
        setProblem({ text: sentence(e.message) });
      } else if (e instanceof ApiError && e.status < 500) {
        setProblem({ text: `Not sent: ${e.message}` }); // refused before anything went out
      } else {
        // No answer (and no outbox on this device), or a server error: it may have gone out. The key makes sending
        // again safe.
        const offline = !navigator.onLine || isNetworkError(e);
        const what = offline ? "No connection to the server" : `Sending failed (${e instanceof Error ? e.message : String(e)})`;
        setProblem({ text: `${what}. The message is kept here: send it again${offline ? " when you are online" : ""}; it never goes out twice.` });
      }
    } finally {
      setSending(false);
    }
  };

  /** ✕: a message changed here asks whether to save it as a draft; one that was not closes as it is (still in the
   * drafts when it was there before, else there is nothing to keep). */
  const close = () => {
    if (draft && edited.current) {
      setClosing(true);
      return;
    }
    done.current = true;
    onClose(wasKept.current);
  };

  const saveAsDraft = () => {
    if (!draft) return;
    if (!saveDraft(keep(draft))) {
      setClosing(false);
      setProblem({ text: "There is no room on this device to keep the draft. Send it, or copy what you wrote before you close it." });
      return;
    }
    done.current = true;
    onClose(true);
  };

  const discard = () => {
    done.current = true;
    if (draft) clearDraft(draft.id);
    onClose(false);
  };

  const taken = new Set(draft ? [draft.to, draft.cc, draft.bcc].flatMap(parseAddresses).map((e) => addressOf(e).toLowerCase()) : []);
  const addressField = { book, taken };

  return (
    <div class="compose">
      <div class="toolbar compose-toolbar">
        <button class="icon-button" title="Close" disabled={sending} onClick={close}>
          <Icon name="close" />
        </button>
        <h2 class="title">{COMPOSE_TITLES[mode]}</h2>
        <span class="spacer" />
        <button class="icon-button" title="Attach files" disabled={!draft || sending} onClick={() => picker.current?.click()}>
          <Icon name="clip" />
        </button>
        <button class="primary send-button" disabled={!draft || sending} onClick={() => send()}>
          {sending ? <span class="spinner on-accent" aria-hidden="true" /> : <Icon name="sent" size={18} />}
          {sending ? "Sending" : "Send"}
        </button>
      </div>
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const input = e.target as HTMLInputElement;
          addFiles(input.files);
          input.value = "";
        }}
      />

      {problem && (
        <div class="banner problem" role="alert">
          <span>{problem.text}</span>
          {problem.sentAlready ? (
            <button class="text-button" disabled={sending} onClick={() => send(true)}>
              Send this version too
            </button>
          ) : (
            <button class="icon-button" title="Dismiss" onClick={() => setProblem(null)}>
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
      )}

      {!online && draft && (
        <div class="banner offline-banner" role="status">
          <span>Offline: Send puts the message in the outbox, and it is sent when the app can reach the server.</span>
        </div>
      )}

      {unavailable && <p class="empty">The message could not be loaded, so it cannot be answered or forwarded now.</p>}
      {!draft && !unavailable && (
        <div class="sentinel">
          <span class="spinner" aria-label="Loading" />
        </div>
      )}
      {draft && (
        <div class="compose-form">
          <AddressField label="To" inputRef={toField} value={draft.to} onChange={(to) => update({ to })} {...addressField}>
            {!copies && (
              <button type="button" class="text-button small" onClick={() => setCopies(true)}>
                Cc/Bcc
              </button>
            )}
          </AddressField>
          {copies && <AddressField label="Cc" value={draft.cc} onChange={(cc) => update({ cc })} {...addressField} />}
          {copies && <AddressField label="Bcc" value={draft.bcc} onChange={(bcc) => update({ bcc })} {...addressField} />}
          <label class="compose-field">
            <span>Subject</span>
            <input type="text" value={draft.subject} onInput={(e) => update({ subject: (e.target as HTMLInputElement).value })} />
          </label>

          {lost.length > 0 && (
            <div class="banner">
              <span>Not kept with the draft on this device. Add again: {lost.join(", ")}</span>
              <button class="icon-button" title="Dismiss" onClick={() => changeFiles(files, [])}>
                <Icon name="close" size={16} />
              </button>
            </div>
          )}

          {(draft.archived.length > 0 || files.length > 0) && (
            <ul class="attachments" role="list">
              {draft.archived.map((a) => (
                <li key={`${a.message_id}:${a.part}`}>
                  <Chip
                    name={a.name}
                    bytes={a.size}
                    image={a.mime.startsWith("image/")}
                    disabled={sending}
                    onRemove={() => update({ archived: draft.archived.filter((x) => x !== a) })}
                  />
                </li>
              ))}
              {files.map((f) => (
                <li key={f.id}>
                  <Chip
                    name={f.file.name}
                    bytes={f.file.size}
                    image={f.file.type.startsWith("image/")}
                    disabled={sending}
                    onRemove={() => {
                      changeFiles(files.filter((x) => x.id !== f.id));
                      void dropDraftFile(f.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}

          <textarea
            ref={bodyField}
            class="compose-body"
            aria-label="Message"
            placeholder="Message"
            value={draft.body}
            onInput={(e) => update({ body: (e.target as HTMLTextAreaElement).value })}
          />
        </div>
      )}

      {closing && <CloseSheet kept={wasKept.current} onSave={saveAsDraft} onDiscard={discard} onCancel={() => setClosing(false)} />}
    </div>
  );
}

/** Asked when a message changed here is closed: save it as a draft on the device, discard it (a draft kept before is
 * deleted), or go on writing. A modal dialog: Escape and a tap beside it go on writing. */
function CloseSheet({ kept, onSave, onDiscard, onCancel }: { kept: boolean; onSave: () => void; onDiscard: () => void; onCancel: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    const el = dialog.current;
    if (!el || el.open) return;
    if (typeof el.showModal === "function") el.showModal();
    else el.setAttribute("open", "");
  }, []);

  return (
    <dialog
      ref={dialog}
      class="sheet"
      aria-labelledby={`${id}-title`}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
      onClick={(e) => e.target === dialog.current && onCancel()}
    >
      <div class="sheet-body">
        <h3 id={`${id}-title`}>Save this message as a draft?</h3>
        <p class="muted small">Drafts are kept on this device until you send or delete them: open them from Drafts in the folder list.</p>
        <button class="primary" autofocus onClick={onSave}>
          Save draft
        </button>
        <button class="sheet-danger" onClick={onDiscard}>
          {kept ? "Delete draft" : "Discard"}
        </button>
        <button class="text-button" onClick={onCancel}>
          Keep writing
        </button>
      </div>
    </dialog>
  );
}

function Chip({ name, bytes, image, disabled, onRemove }: { name: string; bytes: number; image: boolean; disabled: boolean; onRemove: () => void }) {
  return (
    <span class="attachment">
      <Icon name={image ? "image" : "clip"} size={16} />
      <span class="attachment-name">{name}</span>
      <span class="muted small">{size(bytes)}</span>
      <button class="chip-remove" title={`Remove ${name}`} disabled={disabled} onClick={onRemove}>
        <Icon name="close" size={14} />
      </button>
    </span>
  );
}

interface AddressFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  book: Suggestion[];
  /** The addresses in the message already (lower case): not suggested again. */
  taken: Set<string>;
  inputRef?: RefObject<HTMLInputElement>;
  children?: ComponentChildren;
}

/** To, Cc or Bcc with suggestions from the address book for the entry being typed: tap one, or pick it with the
 * arrow keys and Enter or Tab. It replaces the entry, and a comma follows for the next address. */
function AddressField({ label, value, onChange, book, taken, inputRef, children }: AddressFieldProps) {
  const fallback = useRef<HTMLInputElement>(null);
  const own = inputRef ?? fallback;
  const [focused, setFocused] = useState(false);
  const [closed, setClosed] = useState(false); // Escape: no suggestions until something is typed
  const [caret, setCaret] = useState(value.length);
  const [active, setActive] = useState(0);
  const placeCaret = useRef<number | null>(null);
  const id = useId();

  const entry = entryAt(value, caret);
  const list = focused && !closed && !entry.text.includes("<") ? suggest(book, entry.text, taken) : [];
  const shown = list.length > 0;
  const current = Math.min(active, list.length - 1);

  useLayoutEffect(() => {
    if (placeCaret.current === null || !own.current) return;
    own.current.setSelectionRange(placeCaret.current, placeCaret.current);
    placeCaret.current = null;
  }, [value]);

  const track = (e: Event) => setCaret((e.currentTarget as HTMLInputElement).selectionStart ?? value.length);

  const choose = (contact: Suggestion) => {
    const next = replaceEntry(value, caret, formatContact(contact));
    placeCaret.current = next.caret;
    setCaret(next.caret);
    setActive(0);
    onChange(next.text);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!shown || e.isComposing) return; // Enter that ends an accent or IME composition stays with it
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      setActive((current + (e.key === "ArrowDown" ? 1 : list.length - 1)) % list.length);
    } else if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
      choose(list[current]);
    } else if (e.key === "Escape") {
      setClosed(true);
    } else {
      return;
    }
    e.preventDefault();
  };

  return (
    <div class="compose-address">
      <label class="compose-field">
        <span>{label}</span>
        <input
          ref={own}
          type="text"
          inputMode="email"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck={false}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={shown}
          aria-controls={`${id}-list`}
          aria-activedescendant={shown ? `${id}-${current}` : undefined}
          value={value}
          onInput={(e) => {
            track(e);
            setClosed(false);
            setActive(0);
            onChange((e.target as HTMLInputElement).value);
          }}
          onKeyDown={onKeyDown}
          onKeyUp={track}
          onClick={track}
          onFocus={(e) => {
            setFocused(true);
            track(e);
          }}
          onBlur={() => setFocused(false)}
        />
        {children}
      </label>
      {shown && (
        <ul id={`${id}-list`} class="suggestions" role="listbox" aria-label={`Addresses for ${label}`}>
          {list.map((c, i) => (
            <li
              key={c.addr}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === current}
              class={i === current ? "active" : undefined}
              // keeps the focus (and the keyboard) in the field
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(c)}
            >
              <span class="suggestion-name">{c.name ?? c.addr}</span>
              {c.name && <span class="suggestion-addr muted small">{c.addr}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function sentence(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) + (/[.!?]$/.test(text) ? "" : ".") : text;
}
